// convertDocDefinitionToHTML.js - FIXED VERSION with proper top spacing
export const convertDocDefinitionToHTML = (docDefinition) => {
  const { content = [], pageMargins = [40, 60, 40, 60] } = docDefinition;

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        * { 
          box-sizing: border-box; 
          margin: 0; 
          padding: 0; 
        }
        
        @page {
          margin: ${pageMargins[1]}px ${pageMargins[2]}px ${pageMargins[3]}px ${pageMargins[0]}px;
          size: A4;
        }
        
        body { 
          font-family: 'Arial', sans-serif; 
          font-size: 12pt; 
          line-height: 2.0;
          color: #000;
          background: white;
        }
        
        /* ✅ CRITICAL FIX: Add top spacing for first page only */
        .page-content:first-of-type {
          padding-top: 180px; /* Adjust this value as needed (60-100px typically works) */
        }
        
        .page-content {
          page-break-after: auto;
          page-break-inside: avoid;
        }
        
        /* ✅ CRITICAL FIX: Control page breaks properly */
        .page-break {
          page-break-before: always;
          break-before: page;
          padding-top: 0 !important; /* Subsequent pages don't need extra top padding */
        }
        
        @media print {
          body { 
            margin: 0; 
            padding: 0;
          }
          
          /* ✅ CRITICAL: First page gets extra top space in print */
          .page-content:first-of-type {
            padding-top: 180px !important;
          }
          
          /* ✅ Prevent blank pages from empty content */
          .page-content:last-child {
            page-break-after: avoid;
          }
          
          /* ✅ Keep content together */
          .row, .col, table {
            page-break-inside: avoid;
          }
        }
        
        @media screen {
          body {
            padding: 20px;
            background: #f5f5f5;
          }
          .page-content {
            background: white;
            padding: 40px;
            margin-bottom: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          
          /* Different spacing for screen preview */
          .page-content:first-of-type {
            padding-top: 40px;
          }
        }
        
        .bold { font-weight: bold; }
        .fs-8 { font-size: 8pt; }
        .fs-9 { font-size: 9pt; }
        .fs-10 { font-size: 10pt; }
        .fs-11 { font-size: 11pt; }
        .fs-12 { font-size: 12pt; }
        
  img + p {
          font-size: 11pt !important;
          font-weight: 500;
          margin-top: 5px;
        }


        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-left { text-align: left; }
        
        .row { 
          display: flex; 
          flex-wrap: wrap; 
          margin-bottom: 8px;
        }
        
        .col { flex: 1; }
        .col-20 { flex: 0 0 20%; }
        .col-33 { flex: 0 0 33.33%; }
        .col-34 { flex: 0 0 34%; }
        .col-50 { flex: 0 0 50%; }
        .col-80 { flex: 0 0 80%; }
        
        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 10px 0;
        }
        
        th, td { 
          padding: 5px; 
          text-align: left; 
          vertical-align: top;
        }
        
        .table-header-row th { 
          font-weight: bold; 
          border-bottom: 1px solid #000;
        }
        
        hr { 
          border: none; 
          border-top: 1px solid #000; 
          margin: 10px 0; 
        }
      </style>
    </head>
    <body>
  `;

  const escapeHtml = (text) => {
    if (typeof text !== "string") return String(text || "");
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const getTextContent = (node) => {
    if (typeof node === "string") return node;
    if (node && typeof node === "object") {
      if (node.text !== undefined) {
        if (typeof node.text === "string") return node.text;
        if (typeof node.text === "object" && node.text.text !== undefined) {
          return node.text.text;
        }
        return String(node.text);
      }
    }
    return String(node || "");
  };

  const processNode = (node) => {
    if (!node) return "";
    if (Array.isArray(node)) {
      return node.map(processNode).join("");
    }

    if (node.canvas) {
      return `<hr />`;
    }

    if (node.image) {
      let src = "";
      if (typeof node.image === "string") {
        src = node.image.startsWith("data:")
          ? node.image
          : `data:image/png;base64,${node.image}`;
      }
      const width = node.width || 60;
      const height = node.height || 30;
      const alignment = node.alignment || "center";
      let style =
        alignment === "right"
          ? "margin-left:auto;"
          : alignment === "left"
          ? "margin-right:auto;"
          : "margin:0 auto;";
      return `<img src="${src}" width="${width}" height="${height}" style="display:block;${style}" />`;
    }

    if (node.text !== undefined) {
      let classes = [];
      if (node.bold) classes.push("bold");
      if (node.fontSize) classes.push(`fs-${node.fontSize}`);
      if (node.alignment) classes.push(`text-${node.alignment}`);
      const text = escapeHtml(getTextContent(node));
      const classStr = classes.length > 0 ? ` class="${classes.join(" ")}"` : "";
      return `<p${classStr}>${text}</p>\n`;
    }

    if (node.columns) {
      const colEls = node.columns.map((col) => {
        let colClass = "col";
        let alignStyle = "";
        if (col.width) {
          if (col.width === "50%") colClass = "col-50";
          else if (col.width === "33%") colClass = "col-33";
          else if (col.width === "34%") colClass = "col-34";
          else if (col.width === "20%") colClass = "col-20";
          else if (col.width === "80%") colClass = "col-80";
        }
        if (col.alignment === "right") alignStyle = "text-align:right;";
        if (col.alignment === "center") alignStyle = "text-align:center;";
        if (col.alignment === "left") alignStyle = "text-align:left;";

        const inner = col.stack
          ? `<div>${col.stack.map(processNode).join("")}</div>`
          : processNode(col);
        return `<div class="${colClass}" style="${alignStyle}">${inner}</div>`;
      });
      return `<div class="row">${colEls.join("")}</div>`;
    }

    if (node.stack) {
      return `<div>${node.stack.map(processNode).join("")}</div>`;
    }

    if (node.table) {
      const { body = [], headerRows = 0, widths = [] } = node.table;
      let tableHtml = "<table>";

      if (widths.length > 0) {
        tableHtml += "<colgroup>";
        widths.forEach((width) => {
          tableHtml += `<col style="width:${width}">`;
        });
        tableHtml += "</colgroup>";
      }

      body.forEach((row, rowIndex) => {
        const isHeader = rowIndex < headerRows;
        const rowTag = isHeader ? "th" : "td";
        const rowClass = isHeader ? "table-header-row" : "";
        tableHtml += `<tr class="${rowClass}">`;

        row.forEach((cell) => {
          let content = "";
          let classes = [];
          if (typeof cell === "string") {
            content = escapeHtml(cell);
          } else if (cell && typeof cell === "object") {
            content = escapeHtml(getTextContent(cell));
            if (cell.bold) classes.push("bold");
            if (cell.fontSize) classes.push(`fs-${cell.fontSize}`);
          }
          const classStr = classes.length > 0 ? ` class="${classes.join(" ")}"` : "";
          tableHtml += `<${rowTag}${classStr}>${content}</${rowTag}>`;
        });
        tableHtml += "</tr>";
      });

      tableHtml += "</table>";
      return tableHtml;
    }

    return "";
  };

  content.forEach((item) => {
    // ✅ Skip the spacer completely - we're using CSS padding instead
    if (item.text === "" && item.margin && item.margin[3] === 100) {
      return;
    }

    if (Array.isArray(item)) {
      // ✅ CRITICAL FIX: Wrap each report in page-content div
      const hasPageBreak = item.some(
        (sub) => sub.text === "" && sub.pageBreak === "before"
      );
      
      if (hasPageBreak) {
        html += `<div class="page-content page-break">`;
      } else {
        html += `<div class="page-content">`;
      }

      item.forEach((sub) => {
        // Skip the pageBreak marker itself
        if (sub.text === "" && sub.pageBreak === "before") return;
        html += processNode(sub);
      });
      
      html += `</div>`;
    } else {
      html += processNode(item);
    }
  });

  html += `
    </body>
    </html>
  `;

  return html;
};